import { FC, useEffect, useRef, useState } from 'react';

import { formatAmsterdamTime } from '@eneco/common/date';
import { Text } from '@eneco/custom-components/Graph';
import { useFormatter } from '@eneco/i18n';
import { useContent } from '@sitecore/common';
import { DynamicPricingTariffsRendering } from '@sitecore/types/DynamicPricingTariffs';
import { TickRendererProps, AxisBottom as VisxAxisBottom, AxisRight as VisxAxisRight } from '@visx/axis';
import { Circle } from '@visx/shape';
import type { ScaleBand, ScaleLinear } from 'd3-scale';

import { PeakPriceLimitRightAxisLabel } from './PeakPriceLimitRightAxisLabel';
import { useDynamicPricingTariffsOptional } from '../../../../hooks/useDynamicPricingTariffs';
import { graphColors } from '../../../../utils/constants';

const AxisBottomLabel: FC<TickRendererProps & { isSelected?: boolean }> = ({
  formattedValue,
  isSelected,
  ...props
}) => {
  return isSelected ? (
    <>
      <Circle cy={props.y} cx={props.x} r={16} fill={graphColors['backgroundDark']} />
      <Text x={props.x} y={props.y} textAnchor="middle" verticalAnchor="middle" fill={graphColors['textInverted']}>
        {formattedValue}
      </Text>
    </>
  ) : (
    <Text x={props.x} y={props.y} textAnchor="middle" verticalAnchor="middle">
      {formattedValue}
    </Text>
  );
};

const getAxisBottomTickRenderer = (selectedValue?: number) => (props: TickRendererProps) => (
  <AxisBottomLabel {...props} isSelected={props.x === selectedValue} />
);

type BottomProps = {
  height: number;
  xScale: ScaleBand<Date>;
  selectedValue?: number;
};

export const AxisBottom: FC<BottomProps> = ({ height, xScale, selectedValue }) => {
  const context = useDynamicPricingTariffsOptional();
  const { date: dateFormatter } = useFormatter();
  const { fields } = useContent<DynamicPricingTariffsRendering>();

  if (!context) return null;
  const { data, selectedView } = context;

  return (
    <VisxAxisBottom
      top={height + 10}
      scale={xScale}
      tickValues={data?.map(item => item.start)}
      tickFormat={amount => {
        const isCurrentHour = data?.find(item => item.start === amount)?.isCurrentHour;
        const isSelected = xScale(amount) === selectedValue;
        if (isSelected && isCurrentHour) return fields?.currentHourText?.value;
        switch (selectedView) {
          case 'day':
            return formatAmsterdamTime(amount, false);
          case 'week':
            return dateFormatter.dayOfWeekShort(amount);
          case 'month':
            return dateFormatter.dayOfMonthNumeric(amount);
          case 'year':
            return dateFormatter.monthShort(amount);
          default:
            return formatAmsterdamTime(amount);
        }
      }}
      tickComponent={getAxisBottomTickRenderer(selectedValue)}
      hideAxisLine
      hideTicks
    />
  );
};

const AxisRightTick = ({ formattedValue, ...props }: { formattedValue?: string }) => (
  <Text {...props}>{formattedValue}</Text>
);

type RightProps = {
  yScale: ScaleLinear<number, number, never>;
  threshold?: number;
};

export const AxisRight: FC<RightProps> = ({ yScale, threshold }) => {
  const { currency } = useFormatter();
  const [width, setWidth] = useState(50);

  const ref = useRef<SVGGraphicsElement>(null);

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.getBoundingClientRect().width + 24);
    }
  }, [ref]);

  return (
    <div>
      <svg height="100%" width={width}>
        <g ref={ref}>
          <VisxAxisRight
            hideAxisLine
            hideTicks
            scale={yScale}
            tickFormat={(amount, i) => {
              if (i === yScale.ticks(8).length - 1 || i % 2 !== 0) return undefined;
              return currency.euro(amount.valueOf());
            }}
            tickComponent={AxisRightTick}
            tickValues={yScale.ticks(8)}
          />

          {threshold ? <PeakPriceLimitRightAxisLabel yScale={yScale} threshold={threshold} /> : null}
        </g>
      </svg>
    </div>
  );
};
